import React, { useEffect, useState } from 'react';
import { authService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { getDisplayName } from '../utils/displayName';

const ProfileSettings: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [displayName, setDisplayName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [title, setTitle] = useState('');
  const [phone, setPhone] = useState('');
  const [team, setTeam] = useState('');
  const [timezone, setTimezone] = useState('');
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifySms, setNotifySms] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        setLoading(true);
        const profile = await authService.getProfile();
        if (!active) return;
        setDisplayName(profile.display_name || '');
        setFirstName(profile.first_name || '');
        setLastName(profile.last_name || '');
        setTitle(profile.title || '');
        setPhone(profile.phone || '');
        setTeam(profile.team || '');
        setTimezone(profile.timezone || '');
        setNotifyEmail(profile.notify_email ?? true);
        setNotifySms(profile.notify_sms ?? false);
      } catch (err: any) {
        if (!active) return;
        setError(err?.response?.data?.error || 'Unable to load profile');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await authService.updateProfile({
        display_name: displayName,
        first_name: firstName,
        last_name: lastName,
        title,
        phone,
        team,
        timezone,
        notify_email: notifyEmail,
        notify_sms: notifySms,
      });
      await refreshUser();
      setSuccess('Profile updated.');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 text-sm text-gray-500">
        Loading profile...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Profile</h1>
        <p className="text-sm text-gray-600 dark:text-slate-300 mt-1">
          This is how your name appears across advisor messages and admin tools.
        </p>
        {user && (
          <p className="text-xs text-gray-500 mt-2">Current display: {getDisplayName(user)}</p>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-md text-sm">
          {success}
        </div>
      )}

      <form onSubmit={handleSave} className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
          <h2 className="text-lg font-medium text-slate-900 dark:text-slate-50">Profile details</h2>
        </div>
        <div className="p-6 space-y-5">
          <div>
          <label className="block text-sm font-semibold text-gray-800 mb-1">Display name</label>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
            placeholder="Louis Muhammad"
          />
          <p className="text-xs text-gray-500 mt-1">Overrides first and last name in the UI.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">First name</label>
            <input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
              placeholder="Louis"
            />
            </div>
            <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">Last name</label>
            <input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
              placeholder="Rahman"
            />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
              placeholder="Advisor"
            />
            </div>
            <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">Phone</label>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
              placeholder="+1 555 123 4567"
            />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">Team</label>
            <input
              value={team}
              onChange={(e) => setTeam(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
              placeholder="Advising"
            />
            </div>
            <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">Timezone</label>
            <input
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
              placeholder="America/New_York"
            />
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 p-4 space-y-3">
            <p className="text-sm font-semibold text-gray-800">Notifications</p>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={notifyEmail}
                onChange={(e) => setNotifyEmail(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-amber-500 focus:ring-amber-400"
              />
              Email notifications
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={notifySms}
                onChange={(e) => setNotifySms(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-amber-500 focus:ring-amber-400"
              />
              SMS notifications
            </label>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
            className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 px-5 py-2.5 text-sm disabled:opacity-60"
            >
              {saving ? 'Saving...' : 'Save profile'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProfileSettings;
